import { Check } from 'lucide-react';
import { cx } from './cx';

export type StepperItem = { num: number; label: string; done: boolean; current: boolean; onGo?: () => void; partial?: boolean };

/**
 * Vertical numbered step list: done = filled navy check, partial (visited, questions left) = amber number,
 * current = ringed number on a tinted row. Steps with onGo are buttons.
 */
export function Stepper({ items }: { items: StepperItem[] }) {
  return (
    <ol className="m-0 flex list-none flex-col gap-1 p-0">
      {items.map((st) => {
        const circle = (
          <span
            aria-hidden="true"
            className={cx(
              'box-border flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs',
              st.done ? 'bg-brand text-white'
                : st.current ? 'border-2 border-brand font-bold text-brand'
                : st.partial ? 'border-[1.5px] border-warn-line bg-warn-bg font-semibold text-warn-fg'
                : 'border-[1.5px] border-line-btn font-semibold text-muted',
            )}
          >
            {st.done ? <Check size={14} strokeWidth={3} /> : st.num}
          </span>
        );
        const label = (
          <span className={cx('text-sm', st.current ? 'font-semibold text-ink' : st.done || st.partial ? 'text-ink-2' : 'text-muted')}>
            {st.label}
            {st.done && <span className="sr-only"> (done)</span>}
            {st.partial && !st.current && <span className="sr-only"> (partly done)</span>}
          </span>
        );
        const row = cx('flex min-h-11 w-full items-center gap-3 rounded-[10px] px-2.5 py-1.5 text-left', st.current ? 'bg-brand-tint' : 'bg-transparent');
        return (
          <li key={st.num}>
            {st.onGo && !st.current ? (
              <button type="button" onClick={st.onGo} className={cx(row, 'border-0 text-ink hover:bg-panel')}>{circle}{label}</button>
            ) : (
              <div aria-current={st.current ? 'step' : undefined} className={row}>{circle}{label}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
